import type { PublicProduct } from '../graphql/public'
import { DIETARY_LABEL, SPICE_LABEL, dietaryOf, spiceLevel } from '../lib/catalog'
import { ChiliIcon, CrescentIcon, LeafIcon } from './icons'

// The kiosk's quiet "icon + label" markers (never a coloured chip): the spice level
// as n chilies + its label, then the first dietary attribute. Composed in that order
// because that is the order the kiosk composes them in.

interface MarkerProps {
  icon: React.ReactNode
  label: string
  size: number
}

function Marker({ icon, label, size }: MarkerProps) {
  return (
    <span
      className="inline-flex items-center space-x-[8px] font-secondary font-normal text-gray-600"
      style={{ fontSize: size, lineHeight: 1.2 }}
    >
      {icon}
      <span>{label}</span>
    </span>
  )
}

// `kinds="dietary"` drops the spice marker: the extras column (build-guide §4) is too
// narrow for a side's name + two markers + price, and a side's spice level is the
// least valuable marker on the board.
export function Markers({
  product,
  size,
  kinds = 'all',
}: {
  product: PublicProduct
  size: number
  kinds?: 'all' | 'dietary'
}) {
  const spice = kinds === 'all' ? spiceLevel(product) : 0
  const dietary = dietaryOf(product)
  if (!spice && !dietary) return null

  const chilies = []
  for (let i = 0; i < spice; i++) chilies.push(<ChiliIcon key={i} size={size} />)

  return (
    <span className="inline-flex items-center space-x-[28px]">
      {spice ? (
        <Marker
          size={size}
          label={SPICE_LABEL[spice]}
          icon={<span className="inline-flex space-x-[1px]">{chilies}</span>}
        />
      ) : null}
      {dietary ? (
        <Marker
          size={size}
          label={DIETARY_LABEL[dietary]}
          icon={dietary === 'HALAL' ? <CrescentIcon size={size} /> : <LeafIcon size={size} />}
        />
      ) : null}
    </span>
  )
}
